/**
 * India Public Holidays Configuration
 * 
 * This file manages public holidays dynamically from API.
 * Falls back to static holidays if API fails.
 */

import { fetchHolidaysAPI } from '../Action/api';

/**
 * Fallback India Public Holidays for 2026 (used if API fails)
 * Format: YYYY-MM-DD
 */
const FALLBACK_HOLIDAYS_2026 = [
  { date: '2026-01-26', name: 'Republic Day 🇮🇳', day: 'Monday' },
  { date: '2026-03-04', name: 'Holi', day: 'Wednesday' },
  { date: '2026-03-21', name: 'Id-ul-Fitr (Eid)', day: 'Saturday', note: '*Subject to moon sighting' },
  { date: '2026-03-31', name: 'Mahavir Jayanti', day: 'Tuesday' },
  { date: '2026-04-03', name: 'Good Friday', day: 'Friday' },
  { date: '2026-05-01', name: 'Buddha Purnima', day: 'Friday' },
  { date: '2026-05-27', name: 'Id-ul-Zuha (Bakrid)', day: 'Wednesday', note: '*Subject to moon sighting' },
  { date: '2026-06-26', name: 'Muharram', day: 'Friday', note: '*Subject to moon sighting' },
  { date: '2026-08-15', name: 'Independence Day 🇮🇳', day: 'Saturday' },
  { date: '2026-09-04', name: 'Milad-un-Nabi', day: 'Friday', note: '*Subject to moon sighting' },
  { date: '2026-10-02', name: 'Mahatma Gandhi Jayanti 🇮🇳', day: 'Friday' },
  { date: '2026-10-20', name: 'Dussehra (Vijayadashami)', day: 'Tuesday' },
  { date: '2026-11-08', name: 'Diwali (Deepavali)', day: 'Sunday' },
  { date: '2026-11-24', name: 'Guru Nanak Jayanti', day: 'Tuesday' },
  { date: '2026-12-25', name: 'Christmas 🎄', day: 'Friday' }
];

/**
 * Configuration for holiday checking
 */
export const HOLIDAY_CONFIG = {
  // Enable/disable holiday checking
  ENABLED: true,
  
  // Current year's holidays (will be populated from API)
  CURRENT_YEAR: 2026,
  
  // Holidays list (will be populated from API or fallback)
  HOLIDAYS: [],
  
  // API loaded flag
  API_LOADED: false,
  
  // Loading state
  IS_LOADING: false
};

/**
 * Fetch holidays from API and update configuration
 * @param {string} countryCode - Country code (default: 'IN')
 * @returns {Promise<void>}
 */
export const loadHolidaysFromAPI = async (countryCode = 'IN') => {
  if (HOLIDAY_CONFIG.IS_LOADING) {
    console.log('Holiday API already loading, skipping...');
    return;
  }
  
  try {
    HOLIDAY_CONFIG.IS_LOADING = true;
    console.log('Loading holidays from API...');
    
    const apiData = await fetchHolidaysAPI(countryCode);
    
    // Update holidays list from API
    HOLIDAY_CONFIG.HOLIDAYS = apiData.holidays.map(holiday => ({
      date: holiday.date,
      name: holiday.name,
      id: holiday.id,
      countryCode: holiday.countryCode
    }));
    
    HOLIDAY_CONFIG.API_LOADED = true;
    console.log(`Loaded ${HOLIDAY_CONFIG.HOLIDAYS.length} holidays from API`);
    
    // Return weekend configuration for use in workingDaysConfig
    return {
      holidays: HOLIDAY_CONFIG.HOLIDAYS,
      saturdayWorking: apiData.saturdayWorking,
      sundayWorking: apiData.sundayWorking
    };
  } catch (error) {
    console.error('Error loading holidays from API, using fallback:', error);
    // Use fallback holidays
    HOLIDAY_CONFIG.HOLIDAYS = FALLBACK_HOLIDAYS_2026;
    HOLIDAY_CONFIG.API_LOADED = false;
    
    return {
      holidays: HOLIDAY_CONFIG.HOLIDAYS,
      saturdayWorking: false,
      sundayWorking: false
    };
  } finally {
    HOLIDAY_CONFIG.IS_LOADING = false;
  }
};

/**
 * Check if a given date is a public holiday
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date is a public holiday
 */
export const isPublicHoliday = (date) => {
  if (!HOLIDAY_CONFIG.ENABLED) {
    return false;
  }
  
  // Format date as YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  
  // Check if the date matches any holiday
  return HOLIDAY_CONFIG.HOLIDAYS.some(holiday => holiday.date === dateStr);
};

/**
 * Get holiday details for a given date
 * @param {Date} date - The date to check
 * @returns {Object|null} - Holiday details or null if not a holiday
 */
export const getHolidayDetails = (date) => {
  if (!HOLIDAY_CONFIG.ENABLED) {
    return null;
  }
  
  // Format date as YYYY-MM-DD
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const dateStr = `${year}-${month}-${day}`;
  
  // Find the holiday
  return HOLIDAY_CONFIG.HOLIDAYS.find(holiday => holiday.date === dateStr) || null;
};

/**
 * Get all holidays as date strings (YYYY-MM-DD)
 * @returns {Array<string>} - Array of holiday date strings
 */
export const getHolidayDates = () => {
  if (!HOLIDAY_CONFIG.ENABLED) {
    return [];
  }
  
  return HOLIDAY_CONFIG.HOLIDAYS.map(holiday => holiday.date);
};

/**
 * Get all holidays for display (with names and days)
 * @returns {Array<Object>} - Array of holiday objects
 */
export const getAllHolidays = () => {
  if (!HOLIDAY_CONFIG.ENABLED) {
    return [];
  }
  
  return HOLIDAY_CONFIG.HOLIDAYS;
};

/**
 * Count the number of holidays between two dates (inclusive)
 * @param {Date} startDate - Start date
 * @param {Date} endDate - End date
 * @returns {number} - Number of holidays in the range
 */
export const countHolidaysInRange = (startDate, endDate) => {
  if (!HOLIDAY_CONFIG.ENABLED) {
    return 0;
  }
  
  let count = 0;
  const current = new Date(startDate.getTime());
  
  while (current <= endDate) {
    if (isPublicHoliday(current)) {
      count++;
    }
    current.setDate(current.getDate() + 1);
  }
  
  return count;
};

/**
 * Get upcoming holidays from a given date
 * @param {Date} fromDate - The date to start from
 * @param {number} count - Number of upcoming holidays to return (default: 5)
 * @returns {Array<Object>} - Array of upcoming holiday objects
 */
export const getUpcomingHolidays = (fromDate = new Date(), count = 5) => {
  if (!HOLIDAY_CONFIG.ENABLED) {
    return [];
  }
  
  const fromDateStr = fromDate.toISOString().split('T')[0];
  
  return HOLIDAY_CONFIG.HOLIDAYS
    .filter(holiday => holiday.date >= fromDateStr)
    .slice(0, count);
};
